"use client";
import React, { useState, useMemo } from "react";
import {
  Edit3, Trash2, Package, Tag, Layers, Star,
  ShoppingBag, AlertTriangle, CheckCircle,
} from "lucide-react";

// ─── helpers ─────────────────────────────────────────────────────────────────
const uniq = (arr, key) => [...new Set(arr.map((v) => v[key]).filter(Boolean))];
const variantSuffix = (v) => [v.size, v.material, v.color].filter(Boolean).join(", ");

const truncateChars = (str, len = 20) => {
  if (!str) return "";
  return str.length <= len ? str : str.slice(0, len) + "…";
};

// ─── Pill ────────────────────────────────────────────────────────────────────
const Pill = ({ label, selected, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    title={label}
    className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-all whitespace-nowrap
      ${disabled
        ? "border-gray-100 text-gray-300 cursor-not-allowed bg-white"
        : selected
        ? "border-teal-500 bg-teal-50 text-teal-700 shadow-sm"
        : "border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600 bg-white"}`}
  >
    {label}
  </button>
);

// ─── Colour swatch ───────────────────────────────────────────────────────────
const ColourSwatch = ({ color, selected, disabled, onClick }) => {
  const isCSS = /^(#|rgb|hsl|[a-z]+$)/i.test(color.trim());
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={color}
      className={`w-5 h-5 rounded-full border-2 transition-all flex-shrink-0
        ${selected ? "border-teal-500 scale-110 shadow-sm" : "border-gray-200 hover:border-teal-300"}
        ${disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
      style={isCSS ? { background: color } : { background: "#94a3b8" }}
    />
  );
};

// ─── Stock Badge ─────────────────────────────────────────────────────────────
const StockBadge = ({ stock }) => {
  const n = Number(stock);
  if (n > 10)  return <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600"><CheckCircle className="w-3 h-3" />{n} in stock</span>;
  if (n > 0)   return <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-500"><AlertTriangle className="w-3 h-3" />Only {n} left</span>;
  return         <span className="flex items-center gap-1 text-[10px] font-semibold text-red-500"><Package className="w-3 h-3" />Out of stock</span>;
};

// ─── Component ───────────────────────────────────────────────────────────────
const ProductCard = ({
  product,
  categories = [],
  subCategories = [],
  // effectiveStatus: 'Active' | 'Inactive' computed by the parent page, taking into
  // account whether the product's category / sub-category / modal-category (model)
  // is itself Inactive. If this prop is omitted the component falls back to the
  // product's own raw status (keeps this component usable standalone).
  effectiveStatus,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const hasVariants = variants.length > 0;

  const rawIsActive = product.status === "Active" || product.status === "active" || product.status === 1;
  const isActive = effectiveStatus ? effectiveStatus === "Active" : rawIsActive;

  // If the product's own status is "Active" but a parent (category / sub-category /
  // model) is Inactive, the effective status forces it Inactive. In that case the
  // toggle must be disabled — flipping it would be misleading since the product
  // can't actually be reactivated until its parent is reactivated.
  const toggleDisabled = rawIsActive && effectiveStatus === "Inactive";
  const toggleTitle = toggleDisabled
    ? "Inactive because its category, sub-category, or model is inactive"
    : (isActive ? "Set Inactive" : "Set Active");

  const defaultVariant = variants.find((v) => v.isDefault) || variants[0] || null;
  const [selectedSize,     setSelectedSize]     = useState(defaultVariant?.size     || "");
  const [selectedMaterial, setSelectedMaterial] = useState(defaultVariant?.material || "");
  const [selectedColor,    setSelectedColor]    = useState(defaultVariant?.color    || "");

  const activeVariant = useMemo(() => {
    if (!hasVariants) return null;
    return variants.find((v) =>
      (v.size || "") === selectedSize &&
      (v.material || "") === selectedMaterial &&
      (v.color || "") === selectedColor
    ) || null;
  }, [variants, selectedSize, selectedMaterial, selectedColor, hasVariants]);

  const allSizes     = useMemo(() => uniq(variants, "size"),     [variants]);
  const allMaterials = useMemo(() => uniq(variants, "material"), [variants]);
  const allColors    = useMemo(() => uniq(variants, "color"),    [variants]);

  const availableMaterials = useMemo(() => {
    if (!selectedSize) return allMaterials;
    return uniq(variants.filter((v) => (v.size || "") === selectedSize), "material");
  }, [variants, selectedSize, allMaterials]);

  const availableColors = useMemo(() => uniq(
    variants.filter((v) =>
      (!selectedSize     || (v.size     || "") === selectedSize) &&
      (!selectedMaterial || (v.material || "") === selectedMaterial)
    ), "color"
  ), [variants, selectedSize, selectedMaterial]);

  const handleSizeClick = (size) => {
    setSelectedSize(size);
    const nextMats = uniq(variants.filter((v) => (v.size || "") === size), "material");
    const nextMat  = nextMats.includes(selectedMaterial) ? selectedMaterial : (nextMats[0] || "");
    setSelectedMaterial(nextMat);
    const nextCols = uniq(variants.filter((v) => (v.size || "") === size && (v.material || "") === nextMat), "color");
    setSelectedColor(nextCols.includes(selectedColor) ? selectedColor : (nextCols[0] || ""));
  };
  const handleMaterialClick = (mat) => {
    setSelectedMaterial(mat);
    const nextCols = uniq(variants.filter((v) => (v.size || "") === selectedSize && (v.material || "") === mat), "color");
    setSelectedColor(nextCols.includes(selectedColor) ? selectedColor : (nextCols[0] || ""));
  };

  // Display values
  const displayImage = (() => {
    if (hasVariants && activeVariant?.imagePreview) return activeVariant.imagePreview;
    if (Array.isArray(product.images) && product.images.length > 0) return product.images[0];
    return null;
  })();

  const displayPrice = (() => {
    if (hasVariants && activeVariant?.price) return Number(activeVariant.price);
    const p = Number(product.your_price);
    return (!p || p === 0) ? Number(product.max_retail_price) : p;
  })();

  const displayMRP = (() => {
    if (hasVariants && activeVariant?.mrp) return Number(activeVariant.mrp);
    return Number(product.max_retail_price);
  })();

  const displayTitle = (() => {
    const base = product.name || "";
    if (!hasVariants || !activeVariant) return base;
    const suffix = variantSuffix(activeVariant);
    return suffix ? `${base}, ${suffix}` : base;
  })();
  const displayTitleShort = truncateChars(displayTitle, 32);

  const discountPct =
    displayMRP > displayPrice && displayPrice > 0
      ? Math.round(((displayMRP - displayPrice) / displayMRP) * 100)
      : null;

  const resolvedCategoryId    = product.category_id    ?? product.categoryId    ?? "";
  const resolvedSubCategoryId = product.sub_category_id ?? product.subcategory_id ?? product.subCategoryId ?? "";
  const categoryName    = product.category_name    || categories.find((c)    => String(c.id) === String(resolvedCategoryId))?.name || "";
  const subcategoryName = product.subcategory_name || subCategories.find((s) => String(s.id) === String(resolvedSubCategoryId))?.subcat_name || "";
  const categoryNameShort    = truncateChars(categoryName, 14);
  const subcategoryNameShort = truncateChars(subcategoryName, 14);

  const stockValue = activeVariant?.stock != null ? Number(activeVariant.stock) : Number(product.stock ?? product.quantity ?? 0);

  return (
    <div className={`bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm flex flex-col transition-all duration-300 group
      ${isActive
        ? "hover:shadow-lg hover:shadow-teal-100/60 hover:-translate-y-0.5"
        : "opacity-55 grayscale"}`}>

      {/* ── Image ── */}
      <div className="relative w-full h-48 overflow-hidden bg-gray-100 flex-shrink-0">
        {displayImage ? (
          <img
            src={displayImage}
            alt={displayTitle}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        {/* Fallback when no image */}
        <div className={`w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 items-center justify-center ${displayImage ? "hidden" : "flex"}`}>
          <ShoppingBag className="w-12 h-12 text-gray-300" />
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent pointer-events-none" />

        {/* Product title on image */}
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
          <h3 className="text-white font-bold text-[13px] leading-snug drop-shadow truncate whitespace-nowrap overflow-hidden" title={displayTitle}>
            {displayTitleShort}
          </h3>
        </div>

        {/* Discount badge — top left */}
        {discountPct && (
          <div className="absolute top-2.5 left-2.5">
            <span className="flex items-center gap-0.5 bg-teal-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              {discountPct}% OFF
            </span>
          </div>
        )}

        {/* Variants badge — top left (if no discount) */}
        {hasVariants && !discountPct && (
          <div className="absolute top-2.5 left-2.5">
            <span className="flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
              <Layers className="w-2.5 h-2.5" />{variants.length} variants
            </span>
          </div>
        )}

        {/* Inactive-due-to-parent badge — shown whenever the product's own
            status is Active but a parent category/sub-category/model forces
            it Inactive, so it's clear at a glance why it looks disabled. */}
        {toggleDisabled && (
          <div className="absolute bottom-2.5 right-2.5">
            <span className="flex items-center gap-1 bg-red-600/90 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
              Parent Inactive
            </span>
          </div>
        )}

        {/* Status toggle — top right */}
        <div className="absolute top-2.5 right-2.5">
          <label
            className={`relative inline-flex items-center ${toggleDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
            title={toggleTitle}
          >
            <input
              type="checkbox"
              checked={isActive}
              disabled={toggleDisabled}
              onChange={() => !toggleDisabled && onToggleStatus(product.id)}
              className="sr-only peer"
            />
            <div className={`w-10 h-5 rounded-full transition-colors duration-300 shadow-sm backdrop-blur-sm
              ${toggleDisabled ? "bg-gray-300/60" : "bg-gray-300/80 peer-checked:bg-green-500"}`} />
            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 peer-checked:translate-x-5" />
          </label>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-3.5 flex flex-col flex-1 gap-2">

        {/* Category breadcrumb */}
        {categoryName && (
          <div className="flex items-center gap-1 flex-nowrap overflow-hidden min-w-0">
            <Tag className="w-3 h-3 text-teal-500 shrink-0" />
            <span
              className="text-[11px] text-teal-600 font-medium truncate whitespace-nowrap overflow-hidden shrink"
              title={categoryName}
            >
              {categoryNameShort}
            </span>
            {subcategoryName && (
              <>
                <span className="text-[10px] text-gray-300 shrink-0">›</span>
                <span
                  className="text-[11px] text-teal-500 truncate whitespace-nowrap overflow-hidden shrink min-w-0"
                  title={subcategoryName}
                >
                  {subcategoryNameShort}
                </span>
              </>
            )}
          </div>
        )}

        {/* ── Variant Selectors ── */}
        {hasVariants && (
          <div className="space-y-1.5">
            {allSizes.length > 0 && (
              <div>
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Size</span>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {allSizes.map((s) => (
                    <Pill key={s} label={s} selected={selectedSize === s} onClick={() => handleSizeClick(s)} />
                  ))}
                </div>
              </div>
            )}
            {allMaterials.length > 0 && (
              <div>
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Material</span>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {allMaterials.map((m) => (
                    <Pill key={m} label={m} selected={selectedMaterial === m} disabled={!availableMaterials.includes(m)} onClick={() => handleMaterialClick(m)} />
                  ))}
                </div>
              </div>
            )}
            {allColors.length > 0 && (
              <div>
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Colour</span>
                <div className="flex flex-wrap gap-1.5 mt-0.5 items-center">
                  {allColors.map((c) => {
                    const isCSS = /^(#|rgb|hsl)/i.test(c.trim());
                    const avail = availableColors.includes(c);
                    return isCSS ? (
                      <ColourSwatch key={c} color={c} selected={selectedColor === c} disabled={!avail} onClick={() => avail && setSelectedColor(c)} />
                    ) : (
                      <Pill key={c} label={c} selected={selectedColor === c} disabled={!avail} onClick={() => setSelectedColor(c)} />
                    );
                  })}
                </div>
              </div>
            )}
            {hasVariants && !activeVariant && (
              <p className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-lg border border-amber-100">
                No variant matches this combination
              </p>
            )}
          </div>
        )}

        {/* ── Price row ── */}
        <div className="flex items-baseline gap-2 mt-auto pt-2 border-t border-gray-100">
          <span className="text-base font-bold text-gray-900">
            {displayPrice ? `₹${Number(displayPrice).toLocaleString()}` : "—"}
          </span>
          {discountPct && displayMRP > displayPrice && (
            <span className="text-xs text-gray-400 line-through">₹{Number(displayMRP).toLocaleString()}</span>
          )}
        </div>

        {/* ── Footer: stock + actions ── */}
        <div className="flex items-center justify-between">
          <StockBadge stock={stockValue} />
          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit(product)}
              className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 transition-colors"
              title="Edit"
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(product.id)}
              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;